import { useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react';

interface ScrollMetrics {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

const INITIAL_METRICS: ScrollMetrics = { scrollLeft: 0, scrollWidth: 0, clientWidth: 0 };

/** `enabled` is false wherever the table isn't rendered at all (the mobile
 * card list), so the metrics effect re-runs when the layout swaps back and
 * finds the freshly mounted scroll container. */
export function useHorizontalScrollbar(
  containerRef: RefObject<HTMLElement | null>,
  scrollSelector: string,
  enabled = true
) {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollElRef = useRef<HTMLElement | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const [metrics, setMetrics] = useState<ScrollMetrics>(INITIAL_METRICS);

  useEffect(() => () => dragCleanupRef.current?.(), []);

  useEffect(() => {
    const scrollEl = enabled ? containerRef.current?.querySelector<HTMLElement>(scrollSelector) : null;
    scrollElRef.current = scrollEl ?? null;
    if (!scrollEl) {
      setMetrics(INITIAL_METRICS);
      return;
    }

    const updateMetrics = () => {
      setMetrics({ scrollLeft: scrollEl.scrollLeft, scrollWidth: scrollEl.scrollWidth, clientWidth: scrollEl.clientWidth });
    };

    updateMetrics();
    scrollEl.addEventListener('scroll', updateMetrics);
    const resizeObserver = new ResizeObserver(updateMetrics);
    resizeObserver.observe(scrollEl);
    // scrollEl's own border/content box stays fixed (it's the fixed-width
    // scroll viewport) — a data/column change instead resizes the table
    // inside it, which is what actually drives scrollWidth. Without watching
    // it too, paging in wider content wouldn't be noticed.
    const innerEl = scrollEl.querySelector<HTMLElement>('table');
    if (innerEl) resizeObserver.observe(innerEl);

    return () => {
      scrollEl.removeEventListener('scroll', updateMetrics);
      resizeObserver.disconnect();
    };
  }, [containerRef, scrollSelector, enabled]);

  const needsScrollbar = metrics.scrollWidth > metrics.clientWidth + 1;
  const thumbWidthRatio = needsScrollbar ? metrics.clientWidth / metrics.scrollWidth : 1;
  const thumbLeftRatio = needsScrollbar ? metrics.scrollLeft / metrics.scrollWidth : 0;

  const handleThumbPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const scrollEl = scrollElRef.current;
    const track = trackRef.current;
    if (!scrollEl || !track) return;

    const thumbPx = (scrollEl.clientWidth / scrollEl.scrollWidth) * track.clientWidth;
    const draggableRangePx = track.clientWidth - thumbPx;
    const scrollableRangePx = scrollEl.scrollWidth - scrollEl.clientWidth;
    if (draggableRangePx <= 0) return;

    const startX = event.clientX;
    const startScrollLeft = scrollEl.scrollLeft;

    const handlePointerMove = (moveEvent: globalThis.PointerEvent) => {
      const deltaScrollLeft = ((moveEvent.clientX - startX) / draggableRangePx) * scrollableRangePx;
      scrollEl.scrollLeft = Math.max(0, Math.min(startScrollLeft + deltaScrollLeft, scrollableRangePx));
    };
    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      dragCleanupRef.current = null;
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    dragCleanupRef.current = handlePointerUp;
  };

  const handleTrackPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.target !== trackRef.current) return;
    const scrollEl = scrollElRef.current;
    const track = trackRef.current;
    if (!scrollEl || !track) return;

    const trackRect = track.getBoundingClientRect();
    const thumbPx = (scrollEl.clientWidth / scrollEl.scrollWidth) * trackRect.width;
    const clickX = event.clientX - trackRect.left;
    const targetThumbLeft = Math.max(0, Math.min(clickX - thumbPx / 2, trackRect.width - thumbPx));
    const draggableRangePx = trackRect.width - thumbPx;
    const scrollableRangePx = scrollEl.scrollWidth - scrollEl.clientWidth;
    if (draggableRangePx <= 0) return;
    scrollEl.scrollLeft = (targetThumbLeft / draggableRangePx) * scrollableRangePx;
  };

  return {
    trackRef,
    needsScrollbar,
    thumbWidthRatio,
    thumbLeftRatio,
    handleTrackPointerDown,
    handleThumbPointerDown,
  };
}
